import { Box, Text, Button, HStack, Divider, Flex, useToast, VStack, FormControl, FormLabel, Input, FormErrorMessage, Icon, Collapse } from "@chakra-ui/react";
import { processPayment, createPaymentIntent } from '../apis/paymentApi';
import { confirmBooking } from '../apis/checkoutApi';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaShieldAlt, FaCreditCard } from 'react-icons/fa';
import axios from 'axios';

const CartSummaryPayment = ({ total, cartItems, isSubmitting: parentIsSubmitting, setIsSubmitting: parentSetIsSubmitting, userProfile }) => {
  // Check if user is an unlimited member
  const isUnlimitedMember = userProfile && userProfile.membershiptype === 'unlimited' && userProfile.membershipstatus === 'active';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  
  // Stripe hooks
  const stripe = useStripe();
  const elements = useElements();
  
  const [paymentInfo, setPaymentInfo] = useState({
    name: userProfile ? `${userProfile.firstname} ${userProfile.lastname}` : '',
    email: userProfile ? userProfile.email : '',
  });
  
  const [errors, setErrors] = useState({});

  // Base URL for API calls - use environment variable or fallback to localhost for development
const API_URL = process.env.NODE_ENV === 'production'
? (process.env.REACT_APP_API_BASE_URL || 'https://apidev.birdie.house')
: 'http://localhost:3001'; // Use your local backend port

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setPaymentInfo({
      ...paymentInfo,
      [name]: value,
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Validate name
    if (!paymentInfo.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Validate email
    if (!paymentInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(paymentInfo.email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleConfirmBooking = async () => {
    if (cartItems.length === 0) {
      toast({
        title: "Empty cart",
        description: "Please add items to your cart before confirming your booking.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    // If user is unlimited member, skip payment and confirm booking directly
    if (isUnlimitedMember) {
      processBookingConfirmation();
      return;
    }
    
    // Otherwise show payment form
    setShowPaymentForm(true);
  };
  
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      toast({
        title: "Please wait",
        description: "Payment system is still loading.",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please check your payment information and try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    setIsSubmitting(true);
    if (parentSetIsSubmitting) parentSetIsSubmitting(true);
    
    try {
      // Create payment intent with tax included
      const intentResponse = await createPaymentIntent(
        userProfile.id,
        userProfile.membershiptype,
        total * 1.1 // Include tax
      );
      
      if (!intentResponse || !intentResponse.clientSecret) {
        throw new Error('Failed to create payment intent. Please try again.');
      }
      
      // Confirm the payment with Stripe.js
      const { error, paymentIntent } = await stripe.confirmCardPayment(intentResponse.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: paymentInfo.name,
            email: paymentInfo.email,
          },
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Check payment status
      if (paymentIntent.status === 'succeeded') {
        // Process booking with the confirmed payment
        await processBookingWithPayment(paymentIntent.id);
      } else if (paymentIntent.status === 'requires_payment_method') {
        throw new Error('Your card was declined. Please try a different payment method.');
      } else {
        throw new Error(`Payment not successful. Status: ${paymentIntent.status}`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: error.response?.data?.message || error.message || "Payment failed",
        description: "",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    
        navigate('/booking');

    } finally {
      setIsSubmitting(false);
      if (parentSetIsSubmitting) parentSetIsSubmitting(false);
    }
  };
  
  // Helper function to process booking after successful payment
  const processBookingWithPayment = async (paymentIntentId) => {
    try {
      // Update the backend with payment and booking details
      const confirmResponse = await axios.post(`${API_URL}/api/confirm-payment`, {
        paymentIntentId,
        bookingDetails: {
          userId: userProfile.id,
          membershipType: userProfile.membershiptype,
          membershipStatus: userProfile.membershipstatus,
          amount: total,
          paymentType: 'booking',
          cartItems: cartItems
        }
      }, {
        withCredentials: true
      });
      
      if (!confirmResponse.data.success) {
        throw new Error('Payment was processed but booking failed.');
      }
      
      toast({
        title: 'Payment Successful',
        description: 'Your booking has been confirmed.',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
      
      // Redirect to dashboard after successful payment
      navigate('/dashboard');
    } catch (error) {
      console.error('Booking confirmation error:', error);
      throw error;
    }
  };
  
  const processBookingConfirmation = async () => {
    try {
      const response = await confirmBooking(cartItems);
      console.log("response", response);
      if (response.isSuccessful === true) {
        toast({
          title: "Booking confirmed",
          description: response.message,
          status: "success",
          duration: 1000,
          isClosable: true,
          position: "top",
        });
        // Ensure we scroll to top when navigating
        navigate(response.redirect);
        window.scrollTo(0, 0);
      } else {
        toast({
          title: "Booking not confirmed",
          description: response.message,
          status: "warning",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
        navigate(response.redirect);
        window.scrollTo(0, 0);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response.data.message || "There was an error confirming your booking. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      console.error('Error:', error.message);
    } finally {
      setIsSubmitting(false);
      if (parentSetIsSubmitting) parentSetIsSubmitting(false);
    }
  };
  
  return (
    <Box 
      borderRadius="lg"
      p={4}
      bg="white"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.05)"
      mb={6}
    >
      <Text fontSize="lg" fontWeight="bold" mb={4} color="gray.800">Summary</Text>
      <Divider mb={4} />
      
      <HStack justify="space-between" mb={2}>
        <Text fontSize="sm" color="gray.600">Subtotal</Text>
        <Text fontSize="sm" fontWeight="medium">${total.toFixed(2)}</Text>
      </HStack>
      
      <HStack justify="space-between" mb={2}>
        <Text fontSize="sm" color="gray.600">Tax (10%)</Text>
        <Text fontSize="sm" fontWeight="medium">${(total * 0.1).toFixed(2)}</Text>
      </HStack>
      
      <Divider my={3} />
      
      <HStack justify="space-between" mb={4}>
        <Text fontSize="md" fontWeight="bold" color="gray.800">Total</Text>
        <Text fontSize="md" fontWeight="bold" color="#82ca9c">
          {isUnlimitedMember ? 'Free (Unlimited Member)' : `$${(total * 1.1).toFixed(2)}`}
        </Text>
      </HStack>
      
      {!showPaymentForm ? (
        <Flex justify="center" mt={6}>
          <Button 
            onClick={handleConfirmBooking} 
            textDecoration="none" 
            fontFamily="'Montserrat', sans-serif" 
            letterSpacing="-0.015em" 
            fontSize="md" 
            py={6}
            px={8}
            color="#fff" 
            bg="#82ca9c" 
            fontWeight="bold" 
            borderRadius="full" 
            boxShadow="0 2px 4px rgba(130, 202, 156, 0.3)"
            _hover={{ 
              background: '#6db889',
              boxShadow: "0 4px 8px rgba(130, 202, 156, 0.4)",
              transform: "translateY(-2px)"
            }}
            transition="all 0.2s"
            width={["100%", "auto"]}
            isLoading={isSubmitting || parentIsSubmitting}
            loadingText="Confirming"
            isDisabled={cartItems.length === 0}
          >
            {isUnlimitedMember ? "Confirm Free Booking" : "Proceed to Payment"}
          </Button>
        </Flex>
      ) : (
        <Collapse in={showPaymentForm} animateOpacity>
          <Box as="form" onSubmit={handlePaymentSubmit} mt={4}>
            <VStack spacing={3} align="stretch">
              <FormControl isInvalid={errors.name}>
                <FormLabel fontSize="xs" fontWeight="medium">Cardholder Name</FormLabel>
                <Input
                  name="name"
                  value={paymentInfo.name}
                  onChange={handleInputChange}
                  placeholder="Name on card"
                  size="sm"
                  borderRadius="md"
                  _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                />
                <FormErrorMessage fontSize="2xs">{errors.name}</FormErrorMessage>
              </FormControl>
              
              <FormControl isInvalid={errors.email}>
                <FormLabel fontSize="xs" fontWeight="medium">Email</FormLabel>
                <Input
                  name="email"
                  value={paymentInfo.email}
                  onChange={handleInputChange}
                  placeholder="Email address"
                  size="sm"
                  borderRadius="md"
                  _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                />
                <FormErrorMessage fontSize="2xs">{errors.email}</FormErrorMessage>
              </FormControl>
              
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="medium">Card Details</FormLabel>
                <Box
                  p={2}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="gray.200"
                  _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                >
                  <CardElement 
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                  />
                </Box>
              </FormControl>
              
              <Button
                type="submit"
                w="full"
                mt={4}
                py={6}
                bg="#82ca9c"
                color="white"
                fontWeight="bold"
                fontSize="md"
                borderRadius="full"
                _hover={{ bg: "#6db889" }}
                _active={{ bg: "#5ba97c" }}
                boxShadow="0 2px 4px rgba(130, 202, 156, 0.3)"
                isLoading={isSubmitting || parentIsSubmitting}
                loadingText="Processing"
                disabled={!stripe}
                leftIcon={<Icon as={FaCreditCard} />}
              >
                Pay ${(total * 1.1).toFixed(2)}
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowPaymentForm(false)}
                mt={2}
              >
                Back
              </Button>
            </VStack>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default CartSummaryPayment;